import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { EmployeeCSVImport } from "@/components/EmployeeCSVImport";
import { DatevSalaryImport } from "@/components/DatevSalaryImport";
import { EmployeeMasterDataPdfImport } from "@/components/EmployeeMasterDataPdfImport";
import { VehicleCSVImport } from "@/components/VehicleCSVImport";
import { DKVInvoiceUpload } from "@/components/DKVInvoiceUpload";
import { Users, FileSpreadsheet, Car, Fuel, Upload, Download, FileDown, Euro } from "lucide-react";
import { toast } from "sonner";
import {
  exportEmployeesToCSV,
  exportSalaryHistoryToCSV,
  exportVehiclesToCSV,
  exportVehicleCostsToCSV,
  exportBonusHistoryToCSV,
  exportBenefitsHistoryToCSV,
  downloadAsExcel,
  downloadAsCSV,
} from "@/lib/dataExport";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Vehicle {
  id: string;
  name: string;
  license_plate: string | null;
}

export function ImportSettings() {
  const [activeTab, setActiveTab] = useState("employees");
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [exporting, setExporting] = useState<string | null>(null);

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      const { data, error } = await supabase
        .from("vehicles")
        .select("id, name, license_plate")
        .order("name");

      if (error) throw error;
      setVehicles(data || []);
    } catch (error) {
      console.error("Error loading vehicles:", error);
    }
  };

  const handleExport = async (
    exportFn: () => Promise<Record<string, unknown>[]>,
    filename: string,
    format: "xlsx" | "csv"
  ) => {
    setExporting(filename);
    try {
      const data = await exportFn();
      if (format === "xlsx") {
        downloadAsExcel(data, filename);
      } else {
        downloadAsCSV(data, filename);
      }
      toast.success(`${filename} erfolgreich exportiert`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error(error instanceof Error ? error.message : "Export fehlgeschlagen");
    } finally {
      setExporting(null);
    }
  };

  const ExportButton = ({
    exportFn,
    filename,
    label,
  }: {
    exportFn: () => Promise<Record<string, unknown>[]>;
    filename: string;
    label: string;
  }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={exporting === filename}>
          <Download className="h-4 w-4 mr-2" />
          {exporting === filename ? "Exportiere..." : label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={() => handleExport(exportFn, filename, "xlsx")}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Als Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport(exportFn, filename, "csv")}>
          <FileDown className="h-4 w-4 mr-2" />
          Als CSV (.csv)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const importTabs = [
    { value: "employees", label: "Mitarbeiter", icon: Users, description: "CSV-Import von Mitarbeiterdaten" },
    { value: "datev", label: "Datev Gehalt", icon: FileSpreadsheet, description: "Gehaltsabrechnungen aus Datev" },
    { value: "masterdata", label: "Stammdaten", icon: Euro, description: "Festbezug aus Stammdaten-PDF" },
    { value: "vehicles", label: "Fahrzeuge", icon: Car, description: "CSV-Import von Fahrzeugdaten" },
    { value: "dkv", label: "DKV Rechnung", icon: Fuel, description: "DKV-Tankrechnungen importieren" },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Daten Import & Export
          </CardTitle>
          <CardDescription>
            Importieren und exportieren Sie Daten aus verschiedenen Quellen
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-5 h-auto gap-2 bg-transparent p-0">
              {importTabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="flex flex-col items-center gap-2 h-auto py-4 px-3 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground border border-border rounded-lg"
                  >
                    <Icon className="h-6 w-6" />
                    <span className="text-sm font-medium">{tab.label}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            <TabsContent value="employees" className="mt-6 space-y-4">
              <div className="flex flex-wrap gap-2 pb-4 border-b">
                <ExportButton
                  exportFn={exportEmployeesToCSV}
                  filename="Mitarbeiter_Export"
                  label="Mitarbeiter exportieren"
                />
              </div>
              <EmployeeCSVImport />
            </TabsContent>

            <TabsContent value="datev" className="mt-6 space-y-4">
              <div className="flex flex-wrap gap-2 pb-4 border-b">
                <ExportButton
                  exportFn={exportSalaryHistoryToCSV}
                  filename="Gehaltshistorie_Export"
                  label="Gehaltsdaten exportieren"
                />
                <ExportButton
                  exportFn={exportBonusHistoryToCSV}
                  filename="Bonus_Export"
                  label="Bonusdaten exportieren"
                />
                <ExportButton
                  exportFn={exportBenefitsHistoryToCSV}
                  filename="Benefits_Export"
                  label="Benefits exportieren"
                />
              </div>
              <DatevSalaryImport />
            </TabsContent>

            <TabsContent value="masterdata" className="mt-6 space-y-4">
              <EmployeeMasterDataPdfImport />
            </TabsContent>

            <TabsContent value="vehicles" className="mt-6 space-y-4">
              <div className="flex flex-wrap gap-2 pb-4 border-b">
                <ExportButton
                  exportFn={exportVehiclesToCSV}
                  filename="Fahrzeuge_Export"
                  label="Fahrzeuge exportieren"
                />
              </div>
              <VehicleCSVImport onImportComplete={loadVehicles} />
            </TabsContent>

            <TabsContent value="dkv" className="mt-6 space-y-4">
              <div className="flex flex-wrap gap-2 pb-4 border-b">
                <ExportButton
                  exportFn={exportVehicleCostsToCSV}
                  filename="Fahrzeugkosten_Export"
                  label="Fahrzeugkosten exportieren"
                />
              </div>
              <DKVInvoiceUpload 
                vehicles={vehicles} 
                onImportComplete={loadVehicles} 
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
